import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  LineChart,
  BarChart as RechartsBarChart,
  AreaChart,
  Line,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { 
  LineChart as LineChartIcon, 
  BarChart3, 
  AreaChart as AreaChartIcon, 
  GitCompare,
  Download,
  FileImage,
  FileSpreadsheet,
  FileText,
  Calendar as CalendarIcon,
  Eye,
  EyeOff,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from 'next-themes';
import { toast } from '@/components/ui/use-toast';

/**
 * Composant de graphique avancé réutilisable
 * 
 * @param {Object} props
 * @param {Array} props.data - Données du graphique (format: [{name: string, ...}])
 * @param {Array} props.series - Configuration des séries [{key: string, name: string, color: string, type?: 'line'|'bar'|'area'}]
 * @param {string} props.title - Titre du graphique
 * @param {boolean} props.showPeriodSelector - Afficher le sélecteur de période (default: true)
 * @param {boolean} props.showChartTypeSelector - Afficher le sélecteur de type de graphique (default: true)
 * @param {function} props.onPeriodChange - Callback lors du changement de période
 * @param {string} props.defaultPeriod - Période par défaut ('7d', '30d', '3m', '6m', '1y', 'all')
 * @param {string} props.defaultChartType - Type de graphique par défaut ('line', 'bar', 'area', 'composed')
 * @param {Object} props.chartConfig - Configuration additionnelle du graphique
 */
const AdvancedChart = ({
  data = [],
  series = [],
  title = '',
  showPeriodSelector = true,
  showChartTypeSelector = true,
  showExportButton = true,
  showSeriesToggle = true,
  showCustomDateRange = false,
  onPeriodChange,
  defaultPeriod = '30d',
  defaultChartType = 'line',
  chartConfig = {},
}) => {
  const { theme } = useTheme();
  const [selectedPeriod, setSelectedPeriod] = useState(defaultPeriod);
  const [chartType, setChartType] = useState(defaultChartType);
  const [visibleSeries, setVisibleSeries] = useState(() => 
    series.reduce((acc, s) => ({ ...acc, [s.key]: true }), {})
  );
  // Date range removed for simplicity

  // Périodes disponibles
  const periods = [
    { value: '7d', label: '7 derniers jours' },
    { value: '30d', label: '30 derniers jours' },
    { value: '3m', label: '3 derniers mois' },
    { value: '6m', label: '6 derniers mois' },
    { value: '1y', label: '1 an' },
    { value: 'all', label: 'Tout' },
  ];

  // Types de graphiques disponibles
  const chartTypes = [
    { value: 'line', label: 'Ligne', icon: LineChartIcon },
    { value: 'bar', label: 'Barres', icon: BarChart3 },
    { value: 'area', label: 'Aire', icon: AreaChartIcon },
    { value: 'composed', label: 'Combiné', icon: GitCompare },
  ];

  // Gestion du changement de période
  const handlePeriodChange = (value) => {
    setSelectedPeriod(value);
    if (onPeriodChange) {
      onPeriodChange(value);
    }
  };

  // Filtrage intelligent des données selon la période sélectionnée
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Si période = 'all', retourner toutes les données
    if (selectedPeriod === 'all') return data;

    // Custom date range removed

    // Mapping précis selon la période et le type de données
    const periodConfig = {
      '7d': { count: 7, label: 'jours' },
      '30d': { count: 30, label: 'jours' },
      '3m': { count: 3, label: 'mois' },
      '6m': { count: 6, label: 'mois' },
      '1y': { count: 12, label: 'mois' },
    };

    const config = periodConfig[selectedPeriod];
    if (!config) return data;

    // Retourner exactement le nombre de points demandés
    const slicedData = data.slice(-config.count);
    return slicedData;
  }, [data, selectedPeriod]);

  // Toggle visibilité d'une série
  const toggleSeries = (seriesKey) => {
    setVisibleSeries(prev => ({
      ...prev,
      [seriesKey]: !prev[seriesKey]
    }));
  };

  // Filtrer les séries visibles
  const activeSeries = useMemo(() => 
    series.filter(s => visibleSeries[s.key]),
    [series, visibleSeries]
  );

  // Fonctions d'export
  const exportToCSV = () => {
    try {
      const headers = ['Date', ...activeSeries.map(s => s.name)].join(',');
      const rows = filteredData.map(row => 
        [row.name, ...activeSeries.map(s => row[s.key] || '')].join(',')
      );
      const csv = [headers, ...rows].join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `chart-data-${Date.now()}.csv`;
      link.click();
      
      toast({ title: 'Succès', description: 'Données exportées en CSV' });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Échec de l\'export CSV', variant: 'destructive' });
    }
  };

  const exportToJSON = () => {
    try {
      const exportData = {
        title,
        period: selectedPeriod,
        chartType,
        series: activeSeries.map(s => ({ key: s.key, name: s.name })),
        data: filteredData
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `chart-data-${Date.now()}.json`;
      link.click();
      
      toast({ title: 'Succès', description: 'Données exportées en JSON' });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Échec de l\'export JSON', variant: 'destructive' });
    }
  };

  const exportToPNG = () => {
    try {
      const chartElement = document.querySelector('.recharts-wrapper');
      if (!chartElement) {
        toast({ title: 'Erreur', description: 'Graphique non trouvé', variant: 'destructive' });
        return;
      }

      // Pour un vrai export PNG, il faudrait utiliser html2canvas ou similar
      toast({ 
        title: 'Info', 
        description: 'Export PNG nécessite html2canvas (à implémenter)' 
      });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Échec de l\'export PNG', variant: 'destructive' });
    }
  };

  // Configuration des couleurs selon le thème
  const tooltipStyle = {
    backgroundColor: theme === 'dark' ? '#021f17' : 'hsl(var(--background))',
    border: '1px solid hsl(var(--border))',
    color: 'hsl(var(--foreground))',
  };

  // Composant du graphique selon le type sélectionné
  const renderChart = () => {
    const commonProps = {
      data: filteredData,
      margin: chartConfig.margin || { top: 5, right: 30, left: 20, bottom: 5 },
    };

    const axisProps = {
      stroke: theme === 'dark' ? '#4ade80' : 'hsl(var(--muted-foreground))',
      style: { fontSize: '12px' },
    };

    const gridProps = {
      strokeDasharray: '3 3',
      stroke: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'hsl(var(--border))',
    };

    switch (chartType) {
      case 'bar':
        return (
          <RechartsBarChart {...commonProps}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="name" {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ color: 'hsl(var(--foreground))' }} />
            {activeSeries.map((s) => (
              <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} />
            ))}
          </RechartsBarChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              {activeSeries.map((s, i) => (
                <linearGradient key={`gradient-${s.key}`} id={`gradient-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={s.color} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={s.color} stopOpacity={0.1} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="name" {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ color: 'hsl(var(--foreground))' }} />
            {activeSeries.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                fill={`url(#gradient-${s.key})`}
              />
            ))}
          </AreaChart>
        );

      case 'composed':
        return (
          <ComposedChart {...commonProps}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="name" {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ color: 'hsl(var(--foreground))' }} />
            {activeSeries.map((s) => {
              const SeriesType = s.type === 'bar' ? Bar : s.type === 'area' ? Area : Line;
              return (
                <SeriesType
                  key={s.key}
                  type={s.type === 'line' || s.type === 'area' ? 'monotone' : undefined}
                  dataKey={s.key}
                  name={s.name}
                  stroke={s.color}
                  fill={s.type === 'bar' || s.type === 'area' ? s.color : undefined}
                  strokeWidth={s.type === 'line' ? 2 : undefined}
                />
              );
            })}
          </ComposedChart>
        );

      case 'line':
      default:
        return (
          <LineChart {...commonProps}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="name" {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ color: 'hsl(var(--foreground))' }} />
            {activeSeries.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                dot={{ fill: s.color, r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        );
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl">
      {/* Header avec contrôles */}
      <div className="p-4 sm:p-6 border-b border-border space-y-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          {/* Titre */}
          {title && <h2 className="text-xl font-semibold text-foreground">{title}</h2>}

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 flex-wrap">
            {/* Sélecteur de type de graphique */}
            {showChartTypeSelector && (
              <div className="flex gap-1 bg-muted p-1 rounded-lg">
                {chartTypes.map(({ value, label, icon: Icon }) => (
                  <Button
                    key={value}
                    variant="ghost"
                    size="sm"
                    onClick={() => setChartType(value)}
                    className={cn(
                      'px-3 py-1.5 h-auto transition-colors',
                      chartType === value
                        ? 'bg-background shadow-sm'
                        : 'hover:bg-background/50'
                    )}
                    title={label}
                  >
                    <Icon className="h-4 w-4" />
                  </Button>
                ))}
              </div>
            )}

            {/* Sélecteur de période */}
            {showPeriodSelector && (
              <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sélectionner période" />
                </SelectTrigger>
                <SelectContent>
                  {periods.map((period) => (
                    <SelectItem key={period.value} value={period.value}>
                      {period.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Sélecteur de plage personnalisée supprimé pour simplification */}

            {/* Bouton d'export */}
            {showExportButton && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Download className="h-4 w-4" />
                    <span className="hidden sm:inline">Exporter</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Format d'export</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={exportToCSV}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    CSV (Excel)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToJSON}>
                    <FileText className="h-4 w-4 mr-2" />
                    JSON
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToPNG}>
                    <FileImage className="h-4 w-4 mr-2" />
                    PNG (Image)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Toggle des séries (courbes) */}
        {showSeriesToggle && series.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {series.map((s) => (
              <Button
                key={s.key}
                variant={visibleSeries[s.key] ? "secondary" : "outline"}
                size="sm"
                onClick={() => toggleSeries(s.key)}
                className="gap-2 transition-all"
              >
                {visibleSeries[s.key] ? (
                  <Eye className="h-3 w-3" />
                ) : (
                  <EyeOff className="h-3 w-3" />
                )}
                <span className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: s.color }}
                  />
                  {s.name}
                </span>
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Zone du graphique */}
      <div className="p-4 sm:p-6">
        <ResponsiveContainer width="100%" height={chartConfig.height || 350}>
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AdvancedChart;

